import { useEffect, useState } from "react";
import { useGetOrganizationBySlugMutation } from "services/organization-api";
import { useIntakeToken, useToken } from "utils/context";

import { useOrganizationSlug } from "./useOrganizationSlug";

export type SlugValidationState = {
  isValidating: boolean;
  isValid: boolean | null;
  organizationName: string | null;
  error: string | null;
};

/**
 * this hook automatically validates the slug from the URL and redirects if not found
 */
export const useOrganizationSlugValidation = (): SlugValidationState => {
  const [validationState, setValidationState] = useState<SlugValidationState>({
    isValidating: true,
    isValid: null,
    organizationName: null,
    error: null,
  });

  const { accessToken } = useToken();
  const { intakeToken, setIntakeToken } = useIntakeToken();
  const organizationSlug = useOrganizationSlug();
  const [getOrganizationBySlug] = useGetOrganizationBySlugMutation();

  useEffect(() => {
    const validateSlug = async () => {
      if (!organizationSlug) {
        setValidationState({
          isValidating: false,
          isValid: false,
          organizationName: null,
          error: "No organization slug found in URL",
        });
        // redirect to 404 if no slug found
        if (window.navigate404 && typeof window.navigate404 === "function") {
          window.navigate404();
        }
        return;
      }

      // if no authentication tokens are available, skip validation
      // this allows unauthenticated users to access register/login pages
      if (!accessToken && !intakeToken) {
        setValidationState({
          isValidating: false,
          isValid: true, // assume valid for unauthenticated users
          organizationName: null, // we don't know the name yet
          error: null,
        });
        return;
      }

      try {
        setValidationState((prev) => ({ ...prev, isValidating: true }));

        const response = await getOrganizationBySlug({
          accessToken,
          intakeToken,
          slug: organizationSlug,
        }).unwrap();

        setValidationState({
          isValidating: false,
          isValid: true,
          organizationName: response.name,
          error: null,
        });
      } catch (error: unknown) {
        console.error("Organization slug validation failed:", error);

        // check if it's a 401 error (invalid token)
        const is401Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 401;

        // check if it's a 500 error (corrupted token causing server error)
        const is500Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          (error.status === 500 || error.status === "PARSING_ERROR");

        // check original status for RTK Query transformed errors
        const isOriginal401Error =
          error &&
          typeof error === "object" &&
          "originalStatus" in error &&
          error.originalStatus === 401;

        const isOriginal500Error =
          error &&
          typeof error === "object" &&
          "originalStatus" in error &&
          error.originalStatus === 500;

        // check if it's a 404 error
        const is404Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 404;

        // if token is invalid or corrupted, clear it and redirect to login
        if (
          is401Error ||
          is500Error ||
          isOriginal401Error ||
          isOriginal500Error
        ) {
          // log to Sentry for monitoring
          const errorStatus =
            error && typeof error === "object" && "status" in error
              ? error.status
              : "unknown";
          const originalStatus =
            error && typeof error === "object" && "originalStatus" in error
              ? error.originalStatus
              : "unknown";

          console.warn(`[SENTRY] Invalid intake token detected and cleared`, {
            errorType: "invalid_intake_token",
            status: errorStatus,
            originalStatus: originalStatus,
            organizationSlug,
            currentPath: window.location.pathname,
            hasIntakeToken: !!intakeToken,
            hasAccessToken: !!accessToken,
          });

          // clear invalid tokens from context and localStorage
          setIntakeToken(null);

          // redirect to login page for this organization
          if (typeof window !== "undefined" && window.location) {
            const loginUrl = `/intake/${organizationSlug}/auth/login`;
            window.location.href = loginUrl;
          }

          // also set state to allow access to auth pages in case redirect doesn't work immediately
          setValidationState({
            isValidating: false,
            isValid: true, // allow access to auth pages
            organizationName: null,
            error: null,
          });
          return;
        }

        setValidationState({
          isValidating: false,
          isValid: false,
          organizationName: null,
          error: is404Error
            ? `Organization with slug '${organizationSlug}' not found`
            : "Failed to validate organization slug",
        });

        // redirect to 404 for invalid slugs
        if (is404Error) {
          if (window.navigate404 && typeof window.navigate404 === "function") {
            window.navigate404();
          }
        }
      }
    };

    validateSlug();
  }, [
    organizationSlug,
    accessToken,
    intakeToken,
    getOrganizationBySlug,
    setIntakeToken,
  ]);

  return validationState;
};
